import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Avatar,
  alpha,
} from "@mui/material";
import {
  School as SchoolIcon,
  PlayArrow as DemoIcon,
  Cancel as NotBilledIcon,
  Warning as ExpiredIcon,
  AttachMoney as RevenueIcon,
  Schedule as TrainingIcon,
  ArrowForward as ArrowForwardIcon,
  Help as QueryIcon,
  CalendarToday as ScheduleIcon,
  Payment as PaymentIcon,
  Dashboard as DashboardIcon,
  Autorenew as RenewalsIcon,
  Apps as AppsIcon,
  Dns as FullSystemIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const BRAND = crmColors.primary.main;
const BRAND_DARK = crmColors.primary.dark;
const HEADER_BG = alpha(BRAND, 0.08);

const STATUS_TONES = {
  Billed: "success",
  Active: "success",
  Demo: "info",
  "Not Billed": "warning",
  Expired: "error",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const formatCurrency = (n) => {
  const num = Number(n) || 0;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
  return `₹${num.toLocaleString()}`;
};

const SectionCard = memo(function SectionCard({ title, subtitle, icon, action, children, tint }) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2.5,
        border: `1px solid ${alpha(tint || BRAND, 0.18)}`,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${alpha(tint || BRAND, 0.12)}`,
          bgcolor: tint ? alpha(tint, 0.06) : HEADER_BG,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(tint || BRAND, 0.14),
              color: tint || BRAND_DARK,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: "0.84rem" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: "block" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Box>
      <Box>{children}</Box>
    </Paper>
  );
});

const ViewAllBtn = memo(function ViewAllBtn({ onClick }) {
  return (
    <Button
      size="small"
      endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
      onClick={onClick}
      sx={{
        textTransform: "none",
        fontSize: "0.72rem",
        fontWeight: 600,
        color: BRAND_DARK,
        minWidth: 0,
        py: 0.2,
        px: 0.75,
      }}
    >
      View all
    </Button>
  );
});

const EmptyState = ({ label }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 2, textAlign: "center" }}>
    {label}
  </Typography>
);

const KpiTile = memo(function KpiTile({
  title,
  value,
  icon,
  color,
  prefix = "",
  suffix = "",
  hint,
  onClick,
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        px: 1.75,
        py: 1.85,
        borderRadius: 2.5,
        cursor: onClick ? "pointer" : "default",
        border: `1.5px solid ${color}`,
        background: `linear-gradient(145deg, ${alpha(color, 0.14)} 0%, ${alpha(color, 0.04)} 42%, #ffffff 100%)`,
        boxShadow: "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          top: -28,
          right: -20,
          width: 88,
          height: 88,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(color, 0.22)} 0%, transparent 70%)`,
          pointerEvents: "none",
        },
        "&:hover": onClick
          ? {
              transform: "translateY(-3px)",
              borderColor: color,
              boxShadow: `0 10px 24px -14px ${alpha(color, 0.4)}`,
            }
          : undefined,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: color,
            color: "#fff",
            flexShrink: 0,
            boxShadow: `0 8px 18px ${alpha(color, 0.35)}`,
            border: `2px solid ${alpha("#fff", 0.55)}`,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: alpha(color, 0.85),
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mb: 0.35,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.55rem", md: "1.85rem" },
              lineHeight: 1.05,
              color: crmColors.text.primary,
              letterSpacing: "-0.03em",
            }}
          >
            <Box component="span" sx={{ color, fontWeight: 800 }}>
              {prefix}
            </Box>
            {typeof value === "number" ? value.toLocaleString() : value}
            {suffix ? (
              <Box component="span" sx={{ color, fontWeight: 700, fontSize: "0.72em", ml: 0.15 }}>
                {suffix}
              </Box>
            ) : null}
          </Typography>
          {hint && (
            <Typography
              sx={{
                mt: 0.4,
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "text.secondary",
                lineHeight: 1.2,
              }}
            >
              {hint}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
});

const MiniMetric = memo(function MiniMetric({ label, value, color, icon, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        border: `1.5px solid ${color}`,
        bgcolor: alpha(color, 0.06),
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 0.12s ease, border-color 0.12s ease",
        "&:hover": onClick
          ? { bgcolor: alpha(color, 0.12), borderColor: color }
          : undefined,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: color,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", display: "block", lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary", lineHeight: 1.2 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
});

const CHIP_TONES = {
  success: {
    color: "#fff",
    border: crmColors.success.dark,
    bg: `linear-gradient(135deg, ${crmColors.success.main} 0%, ${crmColors.success.dark} 100%)`,
    glow: alpha(crmColors.success.dark, 0.28),
  },
  warning: {
    color: "#fff",
    border: crmColors.warning.dark,
    bg: `linear-gradient(135deg, ${crmColors.warning.main} 0%, ${crmColors.warning.dark} 100%)`,
    glow: alpha(crmColors.warning.dark, 0.28),
  },
  error: {
    color: "#fff",
    border: crmColors.error.dark,
    bg: `linear-gradient(135deg, ${crmColors.error.main} 0%, ${crmColors.error.dark} 100%)`,
    glow: alpha(crmColors.error.dark, 0.28),
  },
  info: {
    color: "#fff",
    border: crmColors.info.dark,
    bg: `linear-gradient(135deg, ${crmColors.info.main} 0%, ${crmColors.info.dark} 100%)`,
    glow: alpha(crmColors.info.dark, 0.28),
  },
  primary: {
    color: "#fff",
    border: BRAND_DARK,
    bg: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    glow: alpha(BRAND_DARK, 0.3),
  },
  default: {
    color: "#fff",
    border: crmColors.secondary.dark,
    bg: `linear-gradient(135deg, ${crmColors.secondary.main} 0%, ${crmColors.secondary.dark} 100%)`,
    glow: alpha(crmColors.secondary.dark, 0.22),
  },
};

const SoftChip = memo(function SoftChip({ label, tone = "default", dot = true }) {
  const style = CHIP_TONES[tone] || CHIP_TONES.default;
  return (
    <Chip
      label={label}
      size="small"
      icon={
        dot ? (
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: style.color,
              boxShadow: `0 0 0 3px ${alpha("#fff", 0.28)}`,
              ml: "8px !important",
              mr: "-2px !important",
            }}
          />
        ) : undefined
      }
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: "0.66rem",
        letterSpacing: "0.03em",
        textTransform: "capitalize",
        color: style.color,
        background: style.bg,
        border: `1.5px solid ${style.border}`,
        borderRadius: 0.6,
        boxShadow: "none",
        "& .MuiChip-label": { px: 1, py: 0 },
        "& .MuiChip-icon": { color: "inherit", marginLeft: "7px", marginRight: "-2px" },
      }}
    />
  );
});

const RowItem = memo(function RowItem({ primary, secondary, right, chip, onClick, accent }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.75,
        py: 1.05,
        cursor: onClick ? "pointer" : "default",
        borderBottom: `1px solid ${crmColors.divider}`,
        transition: "background-color 0.12s ease",
        "&:last-child": { borderBottom: 0 },
        "&:hover": onClick ? { bgcolor: alpha(accent || BRAND, 0.04) } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 650, fontSize: "0.8rem", lineHeight: 1.25 }} noWrap>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block" }} noWrap>
            {secondary}
          </Typography>
        )}
      </Box>
      {right}
      {chip}
    </Box>
  );
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSchools: 0,
    demoSchools: 0,
    billedAppSchools: 0,
    billedFullSystemSchools: 0,
    notBilledSchools: 0,
    expiredSchools: 0,
    monthlyRevenue: 0,
    activeTraining: 0,
  });
  const [recentSchools, setRecentSchools] = useState([]);
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [pendingQueries, setPendingQueries] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [pendingInstallments, setPendingInstallments] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getAdminDashboardStats();
    if (!result.success || !result.data) {
      toast.error(result.error || "Failed to load dashboard");
      setLoading(false);
      return;
    }

    const d = result.data;
    const n = (v) => Number(v) || 0;

    setStats({
      totalSchools: n(d.total_schools),
      demoSchools: n(d.demo_schools),
      billedAppSchools: n(d.billed_app_schools) || n(d.billed_schools),
      billedFullSystemSchools: n(d.billed_full_system_schools),
      notBilledSchools: n(d.not_billed_schools),
      expiredSchools: n(d.expired_schools),
      monthlyRevenue: n(d.monthly_revenue),
      activeTraining: 0,
    });

    setRecentSchools(
      (d.recent_schools || []).map((s) => ({
        id: s.school_code,
        name: s.school_name,
        status: s.status || "Active",
        joinDate: s.date_added,
      }))
    );
    setUpcomingRenewals(
      (d.upcoming_renewals || []).map((r) => ({
        id: r.order_id,
        name: r.school_name,
        daysLeft: r.days_left,
        amount: n(r.renewal_rate),
      }))
    );
    setPendingQueries(
      (d.pending_queries || []).map((q) => ({
        id: q.query_id,
        schoolName: q.school_code,
        query: q.query,
        date: q.date_added,
        priority: "Medium",
      }))
    );
    setSchedules(Array.isArray(d.schedules) ? d.schedules : []);
    setPendingInstallments(
      (d.pending_installments || []).map((o) => ({
        id: o.order_id,
        schoolName: o.school_name,
        amount: n(o.amount_due),
        dueDate: o.order_date,
        installmentNo: o.invoice_id || "—",
        status: n(o.amount_paid) > 0 ? "Due Soon" : "Pending",
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const statusTone = (status) => STATUS_TONES[status] || "default";
  const priorityTone = (priority) => (priority === "High" ? "error" : priority === "Medium" ? "warning" : "default");
  const installmentTone = (status) =>
    status === "Overdue" ? "error" : status === "Due Soon" ? "warning" : "default";

  return (
    <PageContentLayout
      title="CRM Dashboard"
      subtitle="Manage your school clients and grow your business"
      barSubtitle={`${stats.totalSchools} schools · ${formatCurrency(stats.monthlyRevenue)} this month`}
      darkBar
      contentPadding={2}
      breadcrumbs={[
        {
          label: "CRM Dashboard",
          icon: <DashboardIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      showBack={false}
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon sx={{ fontSize: 17 }} />}
            onClick={() => navigate("/schools/search")}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 700,
              height: 36,
              px: 1.5,
              borderColor: alpha("#fff", 0.5),
              color: "#fff",
              bgcolor: alpha("#fff", 0.1),
              "&:hover": {
                borderColor: "#fff",
                bgcolor: alpha("#fff", 0.18),
              },
            }}
          >
            Search School
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate("/schools?add=1")}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 800,
              px: 1.75,
              height: 36,
              letterSpacing: "0.01em",
              bgcolor: crmColors.energy.main,
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.45)}`,
              boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.35)}`,
              "&:hover": {
                bgcolor: crmColors.energy.dark,
                boxShadow: `0 6px 16px ${alpha(crmColors.energy.dark, 0.4)}`,
              },
              "& .MuiButton-startIcon": {
                mr: 0.75,
                "& > *:nth-of-type(1)": {
                  fontSize: 18,
                },
              },
            }}
          >
            Add New School
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <LinearProgress sx={{ width: "55%", maxWidth: 280, borderRadius: 1 }} />
        </Box>
      ) : (
        <Box>
          {/* Primary KPIs */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 1.75,
            }}
          >
            <KpiTile
              title="Total Schools"
              value={stats.totalSchools}
              hint="All registered schools"
              icon={<SchoolIcon sx={{ fontSize: 20 }} />}
              color={BRAND}
              onClick={() => navigate("/schools")}
            />
            <KpiTile
              title="Billed(App)"
              value={stats.billedAppSchools}
              hint="App product active"
              icon={<AppsIcon sx={{ fontSize: 20 }} />}
              color={crmColors.success.main}
              onClick={() => navigate("/schools/billed")}
            />
            <KpiTile
              title="Billed(Full System)"
              value={stats.billedFullSystemSchools}
              hint="Full system active"
              icon={<FullSystemIcon sx={{ fontSize: 20 }} />}
              color={crmColors.info.main}
              onClick={() => navigate("/schools/billed")}
            />
            <KpiTile
              title="Service Expired"
              value={stats.expiredSchools}
              hint="Validity ended"
              icon={<ExpiredIcon sx={{ fontSize: 20 }} />}
              color={crmColors.error.main}
              onClick={() => navigate("/schools/expired")}
            />
          </Box>

          {/* Secondary metrics */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1,
              mb: 1.75,
            }}
          >
            <MiniMetric
              label="Demo"
              value={stats.demoSchools}
              color={crmColors.info.main}
              icon={<DemoIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/schools/demo")}
            />
            <MiniMetric
              label="Not Billed"
              value={stats.notBilledSchools}
              color={crmColors.warning.main}
              icon={<NotBilledIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/schools/not-billed")}
            />
            <MiniMetric
              label="Training"
              value={stats.activeTraining}
              color={crmColors.secondary.main}
              icon={<TrainingIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/training")}
            />
            <MiniMetric
              label="This Month"
              value={formatCurrency(stats.monthlyRevenue)}
              color={BRAND}
              icon={<RevenueIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/billdesk")}
            />
          </Box>

          {/* Activity panels */}
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid item xs={12} md={6}>
              <SectionCard
                title="Pending Queries"
                subtitle={`${pendingQueries.length} open`}
                icon={<QueryIcon sx={{ fontSize: 16 }} />}
                tint={crmColors.warning.main}
                action={<ViewAllBtn onClick={() => navigate("/school-query")} />}
              >
                {pendingQueries.length === 0 ? (
                  <EmptyState label="No pending queries" />
                ) : (
                  pendingQueries.slice(0, 5).map((q) => (
                    <RowItem
                      key={q.id}
                      primary={q.schoolName}
                      secondary={q.query}
                      accent={crmColors.warning.main}
                      onClick={() => navigate("/school-query")}
                      chip={<SoftChip label={q.priority} tone={priorityTone(q.priority)} />}
                      right={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", flexShrink: 0 }}>
                          {formatDate(q.date)}
                        </Typography>
                      }
                    />
                  ))
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard
                title="Schedules"
                subtitle={`${schedules.length} upcoming`}
                icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                tint={crmColors.info.main}
                action={<ViewAllBtn onClick={() => navigate("/training")} />}
              >
                {schedules.length === 0 ? (
                  <EmptyState label="No upcoming schedules" />
                ) : (
                  schedules.slice(0, 5).map((s) => (
                    <RowItem
                      key={s.id}
                      primary={s.title}
                      secondary={`${formatDate(s.date)}${s.time ? ` · ${s.time}` : ""}`}
                      accent={crmColors.info.main}
                      onClick={() => navigate("/training")}
                      chip={s.type ? <SoftChip label={s.type} tone="info" /> : null}
                    />
                  ))
                )}
              </SectionCard>
            </Grid>
          </Grid>

          {/* Installments */}
          <Box sx={{ mb: 1.5 }}>
            <SectionCard
              title="Pending Installments"
              subtitle={`${pendingInstallments.length} due`}
              icon={<PaymentIcon sx={{ fontSize: 16 }} />}
              tint={crmColors.error.main}
              action={<ViewAllBtn onClick={() => navigate("/contracts")} />}
            >
              {pendingInstallments.length === 0 ? (
                <EmptyState label="No pending installments" />
              ) : (
                pendingInstallments.slice(0, 6).map((item) => (
                  <RowItem
                    key={item.id}
                    primary={item.schoolName}
                    secondary={`#${item.installmentNo} · Due ${formatDate(item.dueDate)}`}
                    accent={crmColors.error.main}
                    onClick={() => navigate("/contracts")}
                    right={
                      <Typography
                        sx={{
                          fontWeight: 750,
                          fontSize: "0.82rem",
                          color: crmColors.error.main,
                          mr: 0.5,
                          flexShrink: 0,
                        }}
                      >
                        {formatCurrency(item.amount)}
                      </Typography>
                    }
                    chip={<SoftChip label={item.status} tone={installmentTone(item.status)} />}
                  />
                ))
              )}
            </SectionCard>
          </Box>

          {/* Schools + Renewals */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={7}>
              <SectionCard
                title="Recent Schools"
                subtitle="Latest onboarded"
                icon={<SchoolIcon sx={{ fontSize: 16 }} />}
                action={<ViewAllBtn onClick={() => navigate("/schools")} />}
              >
                {recentSchools.length === 0 ? (
                  <EmptyState label="No recent schools" />
                ) : (
                  recentSchools.slice(0, 6).map((school) => (
                    <RowItem
                      key={school.id}
                      primary={school.name}
                      secondary={`Joined ${formatDate(school.joinDate)}`}
                      onClick={() => navigate("/schools")}
                      chip={<SoftChip label={school.status} tone={statusTone(school.status)} />}
                    />
                  ))
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={5}>
              <SectionCard
                title="Upcoming Renewals"
                subtitle="Next 30 days"
                icon={<RenewalsIcon sx={{ fontSize: 16 }} />}
                tint={crmColors.warning.main}
                action={<ViewAllBtn onClick={() => navigate("/renewals")} />}
              >
                {upcomingRenewals.length === 0 ? (
                  <EmptyState label="No upcoming renewals" />
                ) : (
                  upcomingRenewals.slice(0, 6).map((school) => (
                    <Box
                      key={school.id}
                      onClick={() => navigate("/renewals")}
                      sx={{
                        px: 1.75,
                        py: 1.1,
                        cursor: "pointer",
                        borderBottom: `1px solid ${crmColors.divider}`,
                        "&:last-child": { borderBottom: 0 },
                        "&:hover": { bgcolor: alpha(crmColors.warning.main, 0.04) },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 650, fontSize: "0.8rem" }} noWrap>
                          {school.name}
                        </Typography>
                        <SoftChip
                          label={`${school.daysLeft}d`}
                          tone={school.daysLeft <= 7 ? "error" : "warning"}
                          dot={false}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                          Renewal amount
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: crmColors.success.main }}>
                          {formatCurrency(school.amount)}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, ((30 - school.daysLeft) / 30) * 100))}
                        color={school.daysLeft <= 7 ? "error" : "warning"}
                        sx={{ height: 4, borderRadius: 2, bgcolor: alpha(crmColors.warning.main, 0.12) }}
                      />
                    </Box>
                  ))
                )}
              </SectionCard>
            </Grid>
          </Grid>
        </Box>
      )}
    </PageContentLayout>
  );
};

export default AdminDashboard;
